import { useEffect } from 'react';
import styled from 'styled-components';
import {
  Loading,
  Flex,
  FlexColumn,
  TextField,
  flexColumn,
  Breadcrumbs,
  LoadingButton,
} from '@elementary/common-ui/components';
import { Typography, InputLabel, Autocomplete } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import useAccountSettings from 'shared/hooks/useAccountSettings';
import { SsoSection } from '../Sso';
import { ProvisioningSection } from '../Provisioning';

const Wrapper = styled(FlexColumn)`
  width: 100%;
  gap: 24px;
`;

const Form = styled.form`
  ${flexColumn};
  gap: 16px;
  width: 100%;
`;

const Frame = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.common.gray8};
  border-radius: 6px;
  padding: 32px;
`;

const FieldsContainer = styled(FlexColumn)`
  gap: 38px;
`;

type AccountSettingsForm = {
  timezone: string;
};

const AccountSettings = () => {
  const {
    data: accountSettings,
    isLoading,
    update,
    isUpdating,
  } = useAccountSettings();
  const { handleSubmit, reset, control } = useForm<AccountSettingsForm>({});

  useEffect(() => {
    reset({
      timezone: accountSettings?.timezone || '',
    });
  }, [accountSettings, reset]);

  const onSubmit = (data: AccountSettingsForm) => update(data);

  return isLoading ? (
    <Loading />
  ) : (
    <Wrapper>
      <Breadcrumbs items={['Account', 'Settings']} showTreeButton={false} />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Frame>
          <Flex
            sx={{
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>
              Account settings
            </Typography>
            <FlexColumn>
              <LoadingButton
                loading={isUpdating}
                variant="outlined"
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </LoadingButton>
            </FlexColumn>
          </Flex>
          <FieldsContainer>
            <FlexColumn>
              <InputLabel shrink>Timezone</InputLabel>
              <Controller
                render={({ field }) => (
                  <Autocomplete
                    sx={{ width: '100%' }}
                    options={Intl.supportedValuesOf('timeZone')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="large"
                        placeholder={field.value || 'Select timezone'}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                  />
                )}
                name="timezone"
                control={control}
              />
            </FlexColumn>
          </FieldsContainer>
        </Frame>
      </Form>
      <SsoSection />
      <ProvisioningSection />
    </Wrapper>
  );
};

export default AccountSettings;
